import React from 'react';
import {useTranslation} from 'react-i18next';
import {Avatar, Box, Stack, Tooltip, Typography, useTheme} from '@mui/material';
import {Theme, alpha} from '@mui/material/styles';
import {amber, blueGrey, green} from '@mui/material/colors';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTriangleExclamation} from '@fortawesome/pro-solid-svg-icons';
import dayjs, {Dayjs} from 'dayjs';

import {CalendarEventDetail} from './calendarSources';
import {
    DAY_END_HOUR,
    DAY_START_HOUR,
    PlannerBar,
    PlannerRange,
    PlannerRow,
    PlannerStatus,
} from './plannerModel';

/** Breite der Namensspalte. */
const NAME_COLUMN = 224;

/** Höhe einer Etage im Zeitband. */
const BAR_HEIGHT = 26;

/** Höhe des Ganztagsbands — flacher, es trägt weniger Information. */
const ALL_DAY_HEIGHT = 20;

/** Senkrechter Innenabstand einer Zeile. */
const ROW_PADDING = 8;

/**
 * Ab welcher Balkenbreite (in Prozent) der Titel hineingeschrieben wird.
 *
 * Ein einstündiger Termin ist in der Wochenansicht gut ein Prozent breit; dort stand vorher „GÖ"
 * oder „Upda" — abgeschnittene Wortfragmente, die nichts sagen. Unterhalb der Schwelle trägt der
 * Balken nur Farbe; Titel und Zeitraum stehen im Mauszeiger und im Detail-Dialog.
 */
const LABEL_MIN_WIDTH = 6;

interface Props {
    rows: PlannerRow[];
    days: Dayjs[];
    range: PlannerRange;
    /** Position der Jetzt-Linie in Prozent, oder `null` wenn heute nicht im Blick ist. */
    nowPosition: number | null;
    /** Die aktuelle Uhrzeit als Beschriftung des Strahls. */
    nowLabel: string;
    onSelect: (detail: CalendarEventDetail) => void;
}

/**
 * Die Planungstafel: eine Zeile je Person, waagerechte Zeitachse.
 *
 * **Zur Gestaltung der Balken:** getönte Fläche mit farbiger Kante links und dunkler Schrift —
 * dieselbe Sprache, die der Kalender benutzt. Vollflächige Farbe mit weißer Schrift war der Grund,
 * warum die Tafel wie ein Bausteinkasten aussah: bei sechs Kategorien nebeneinander wird jede
 * Fläche zum Signal, und am Ende schreit alles gleich laut.
 */
export const PlannerBoard: React.FC<Props> = ({
    rows, days, range, nowPosition, nowLabel, onSelect,
}) => {
    const theme = useTheme();
    const {t, i18n} = useTranslation();

    const hours = Array.from(
        {length: DAY_END_HOUR - DAY_START_HOUR},
        (_, i) => DAY_START_HOUR + i);

    /** Hintergrundraster — liegt in jeder Zeile und im Kopf, damit die Spalten fluchten. */
    const grid = (
        <Box sx={{position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'none'}}>
            {days.map((day) => (
                <Box key={day.valueOf()} sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    borderLeft: 1,
                    borderColor: 'divider',
                    bgcolor: dayTint(day, theme),
                }}>
                    {(range === 'day' ? hours : []).map((hour, index) => (
                        <Box key={hour} sx={{
                            flex: 1,
                            minWidth: 0,
                            borderLeft: index === 0 ? 0 : 1,
                            borderColor: alpha(theme.palette.divider, 0.45),
                        }}/>
                    ))}
                </Box>
            ))}
        </Box>
    );

    return (
        <Box>
            {/* Kopfzeile mit den Tagen. Klebt beim Scrollen oben, damit man die Spalte behält. */}
            <Stack direction="row" sx={{
                position: 'sticky',
                top: 0,
                zIndex: 4,
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
            }}>
                <Box sx={{width: NAME_COLUMN, flexShrink: 0, borderRight: 1, borderColor: 'divider'}}/>
                <Box sx={{flexGrow: 1, position: 'relative', minWidth: 0, display: 'flex'}}>
                    {/* Uhrzeit am Kopf des Strahls — ein unbeschrifteter Strich lässt raten. */}
                    {nowPosition !== null && (
                        <Box sx={{
                            position: 'absolute',
                            left: `${nowPosition}%`,
                            bottom: 0,
                            transform: 'translateX(-50%)',
                            zIndex: 5,
                            px: 0.75,
                            py: '1px',
                            borderRadius: '999px',
                            bgcolor: 'error.main',
                            color: 'error.contrastText',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            lineHeight: 1.6,
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                        }}>
                            {nowLabel}
                        </Box>
                    )}
                    {days.map((day) => {
                        const today = day.isSame(dayjs(), 'day');
                        return (
                            <Box key={day.valueOf()} sx={{
                                flex: 1,
                                minWidth: 0,
                                borderLeft: 1,
                                borderColor: 'divider',
                                bgcolor: dayTint(day, theme),
                                px: 1,
                                py: 1,
                            }}>
                                <Stack direction="row" spacing={0.75}
                                       sx={{alignItems: 'baseline', justifyContent: 'center'}}>
                                    <Typography variant="caption" sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        color: today ? 'primary.main' : 'text.secondary',
                                        fontWeight: 600,
                                    }}>
                                        {day.toDate().toLocaleDateString(i18n.language, {weekday: 'short'})}
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        fontWeight: today ? 700 : 500,
                                        color: today ? 'primary.main' : 'text.primary',
                                    }}>
                                        {day.format('DD.MM.')}
                                    </Typography>
                                </Stack>

                                {/* Stundenraster nur im Tagesblick — in der Woche wäre es zu dicht. */}
                                {range === 'day' && (
                                    <Stack direction="row" sx={{mt: 0.5}}>
                                        {hours.map((hour) => (
                                            <Box key={hour} sx={{flex: 1, minWidth: 0, textAlign: 'center'}}>
                                                <Typography variant="caption" color="text.secondary"
                                                            sx={{fontSize: '0.65rem'}}>
                                                    {String(hour).padStart(2, '0')}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Stack>

            <Box sx={{position: 'relative'}}>
                {/*
                  * Der Jetzt-Strahl liegt als eine Bahn über allen Zeilen statt als Strich in
                  * jeder — nur so liest man ihn über die ganze Tafel hinweg als eine Linie. Alles
                  * links davon wird leicht abgedunkelt: „vorbei" braucht man nicht mehr zu planen,
                  * und der Blick landet von selbst auf dem, was noch kommt.
                  */}
                {nowPosition !== null && (
                    <Box sx={{
                        position: 'absolute',
                        left: NAME_COLUMN,
                        right: 0,
                        top: 0,
                        bottom: 0,
                        zIndex: 3,
                        pointerEvents: 'none',
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            left: 0,
                            width: `${nowPosition}%`,
                            top: 0,
                            bottom: 0,
                            bgcolor: alpha(theme.palette.common.black, 0.035),
                        }}/>
                        <Box sx={{
                            position: 'absolute',
                            left: `${nowPosition}%`,
                            top: 0,
                            bottom: 0,
                            width: '2px',
                            bgcolor: 'error.main',
                            boxShadow: `0 0 0 1px ${alpha(theme.palette.error.main, 0.2)}`,
                        }}/>
                    </Box>
                )}

                {rows.map((row, index) => {
                const bandHeight = row.stacks * BAR_HEIGHT + (row.stacks - 1) * 4;
                const rowHeight = ROW_PADDING * 2 + bandHeight
                    + (row.hasAllDay ? ALL_DAY_HEIGHT + 6 : 0);

                return (
                    <Stack key={row.identifier} direction="row" sx={{
                        borderTop: index === 0 ? 0 : 1,
                        borderColor: 'divider',
                        // Zebra: hilft, eine Zeile über die ganze Breite zu halten.
                        bgcolor: index % 2 === 1 ? alpha(theme.palette.action.hover, 0.35) : undefined,
                        '&:hover': {bgcolor: alpha(theme.palette.primary.main, 0.04)},
                        minHeight: Math.max(rowHeight, 52),
                    }}>
                        <Stack direction="row" spacing={1.25} sx={{
                            width: NAME_COLUMN,
                            flexShrink: 0,
                            alignItems: 'center',
                            px: 1.5,
                            borderRight: 1,
                            borderColor: 'divider',
                        }}>
                            <Avatar sx={{
                                width: 30, height: 30, fontSize: '0.75rem', fontWeight: 600,
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: 'primary.main',
                            }}>
                                {initials(row.label)}
                            </Avatar>
                            <Box sx={{minWidth: 0}}>
                                <Typography variant="body2" sx={{
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {row.label}
                                </Typography>
                                {/*
                                  * „Wer kann jetzt?" ist die Frage, für die man eine Planungstafel
                                  * aufmacht — deshalb steht die Antwort am Namen und nicht nur
                                  * irgendwo im Balkenbild.
                                  */}
                                {row.status !== 'unknown' && (
                                    <Stack direction="row" spacing={0.5} sx={{alignItems: 'center'}}>
                                        <Box sx={{
                                            width: 7, height: 7, borderRadius: '50%',
                                            bgcolor: STATUS_COLOR[row.status],
                                            flexShrink: 0,
                                        }}/>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            overflow: 'hidden', textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {statusText(row.status, row.statusUntil, t)}
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>
                            {/* Fehlende Quelle als Zeichen — die Zeile bleibt trotzdem stehen. */}
                            {row.notes.length > 0 && (
                                <Tooltip title={row.notes.join(' · ')}>
                                    <Box component="span" sx={{
                                        color: 'warning.main', display: 'inline-flex', ml: 'auto',
                                    }}>
                                        <FontAwesomeIcon icon={faTriangleExclamation}/>
                                    </Box>
                                </Tooltip>
                            )}
                        </Stack>

                        <Box sx={{
                            flexGrow: 1,
                            position: 'relative',
                            minWidth: 0,
                            py: `${ROW_PADDING}px`,
                        }}>
                            {grid}

                            {row.hasAllDay && (
                                <Box sx={{position: 'relative', height: ALL_DAY_HEIGHT, mb: '6px'}}>
                                    {row.bars.filter((bar) => bar.lane === 'allDay').map((bar) => (
                                        <Bar key={bar.key} bar={bar} onSelect={onSelect}/>
                                    ))}
                                </Box>
                            )}

                            <Box sx={{position: 'relative', height: bandHeight}}>
                                {row.bars.filter((bar) => bar.lane === 'timed').map((bar) => (
                                    <Bar key={bar.key} bar={bar} onSelect={onSelect}
                                         top={bar.stack * (BAR_HEIGHT + 4)} height={BAR_HEIGHT}/>
                                ))}
                            </Box>
                        </Box>
                    </Stack>
                );
                })}
            </Box>
        </Box>
    );
};

/**
 * Ein Balken.
 *
 * Getönte Fläche, farbige Kante links, dunkle Schrift — bei sechs Kategorien nebeneinander bleibt
 * die Tafel damit ruhig, und die Kante trägt die Farbinformation deutlich genug.
 */
const Bar: React.FC<{
    bar: PlannerBar;
    onSelect: (detail: CalendarEventDetail) => void;
    top?: number;
    height?: number;
}> = ({bar, onSelect, top, height}) => (
    <Tooltip title={bar.tooltip} placement="top" arrow>
        <Box onClick={() => onSelect(bar.detail)} sx={{
            position: 'absolute',
            left: `${bar.left}%`,
            width: `${bar.width}%`,
            top: top ?? 0,
            height: height ?? '100%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            px: 0.75,
            borderRadius: '4px',
            bgcolor: alpha(bar.color, bar.free ? 0.08 : 0.18),
            borderLeft: '3px solid',
            borderColor: bar.color,
            // Gestrichelte Umrandung für „frei": blockiert keine Zeit, soll aber sichtbar bleiben.
            outline: bar.free ? `1px dashed ${alpha(bar.color, 0.7)}` : 'none',
            outlineOffset: '-1px',
            overflow: 'hidden',
            zIndex: 2,
            transition: 'background-color 120ms',
            '&:hover': {bgcolor: alpha(bar.color, 0.3)},
        }}>
            {bar.width >= LABEL_MIN_WIDTH && (
                <Typography variant="caption" sx={{
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    lineHeight: 1.2,
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                    {bar.title}
                </Typography>
            )}
        </Box>
    </Tooltip>
);

/** Ampelfarben des Zustands — grün heißt „jetzt ansprechbar". */
const STATUS_COLOR: Record<Exclude<PlannerStatus, 'unknown'>, string> = {
    free: green[600],
    busy: amber[800],
    absent: blueGrey[400],
};

/** Zustand als Text, mit dem Zeitpunkt, bis wann er gilt. */
const statusText = (
    status: PlannerStatus,
    until: string | undefined,
    t: (key: string, options?: Record<string, unknown>) => string,
): string => {
    if (status === 'absent') return t('planner.statusAbsent');
    if (status === 'busy') {
        return until ? t('planner.statusBusyUntil', {time: until}) : t('planner.statusBusy');
    }
    return until ? t('planner.statusFreeUntil', {time: until}) : t('planner.statusFree');
};

/** Hintergrund einer Tagesspalte: heute betont, Wochenende gedämpft. */
const dayTint = (day: Dayjs, theme: Theme): string | undefined => {
    if (day.isSame(dayjs(), 'day')) return alpha(theme.palette.primary.main, 0.06);
    if (day.day() === 0 || day.day() === 6) return alpha(theme.palette.action.disabledBackground, 0.5);
    return undefined;
};

/** Initialen aus „Nachname, Vorname" oder „Vorname Nachname". */
const initials = (label: string): string => {
    const parts = label.replace(',', ' ').split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

export default PlannerBoard;
